import React from "react";
import { Card, Form, Button } from "antd";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import "../Styles/CompetitiveForm.scss";
import { DefinitionTooltip } from "src/components/common";
import { formatNumber } from "src/utils/numberFormat";
import BackButton from "../Components/BackButton";
import BrandFormFields from "../Components/BrandFormFields";
import type { BrandFormRow } from "../Types/SocialListeningTypes";
import {
  MAX_COMPETITORS,
  MIN_COMPETITORS,
  SOCIAL_LISTENING_ROUTES,
} from "../Utils/SocialListeningConstants";

interface CompetitiveFormValues {
  competitors?: BrandFormRow[];
}

const CompetitiveAnalysisForm: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm<CompetitiveFormValues>();
  const rows = Form.useWatch("competitors", form);
  const isComplete =
    (rows?.length ?? 0) >= MIN_COMPETITORS && rows?.every(row => row?.brandName?.trim()) === true;
  const limitsText = t("socialListingAgent.competitiveForm.competitorLimitsTooltip", {
    min: formatNumber(MIN_COMPETITORS, {}, i18n.language),
    max: formatNumber(MAX_COMPETITORS, {}, i18n.language),
  });

  const onFinish = ({ competitors }: CompetitiveFormValues) => {
    if (!competitors || competitors.length < MIN_COMPETITORS) {return;}

    navigate(SOCIAL_LISTENING_ROUTES.loader, {
      state: { analysisType: "competitive_analysis" as const, competitors },
    });
  };

  return (
    <div className="competitive-form-container">
      <div className="competitive-form-header">
        <BackButton
          label={t("socialListingAgent.competitiveForm.back")}
          to={SOCIAL_LISTENING_ROUTES.home}
        />
      </div>

      <div className="competitive-form-title-row">
        <h1 className="competitive-form-title">
          {t("socialListingAgent.competitiveForm.title")}
        </h1>
        <DefinitionTooltip
          label={t("socialListingAgent.competitiveForm.title")}
          info={{ description: limitsText }}
          preferSide="left"
        />
      </div>

      <p className="competitive-form-subtitle">
        {t("socialListingAgent.competitiveForm.subtitle")}
      </p>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        className="competitive-form"
        initialValues={{ competitors: Array.from({ length: MIN_COMPETITORS }, () => ({})) }}
      >
        <Form.List name="competitors">
          {(fields, { add, remove }) => (
            <>
              <div className="competitor-grid">
                {fields.map((field, index) => (
                  <Card key={field.key} className="competitive-form-card" bordered={false}>
                    {fields.length > MIN_COMPETITORS && (
                      <Button
                        type="text"
                        className="competitor-remove"
                        icon={<CloseOutlined />}
                        aria-label={t("socialListingAgent.competitiveForm.removeCompetitor")}
                        onClick={() => remove(field.name)}
                      />
                    )}
                    <h3>
                      {t("socialListingAgent.competitiveForm.competitorTitle", {
                        index: formatNumber(index + 1, {}, i18n.language),
                      })}
                    </h3>

                    <BrandFormFields
                      scope="competitiveForm"
                      handlePlaceholder="@username"
                      namePath={[field.name]}
                    />

                    <p className="competitor-note">
                      {t("socialListingAgent.competitiveForm.socialHandlesOptional")}
                    </p>
                  </Card>
                ))}
              </div>

              <div className="competitive-actions">
                <Button
                  className="button button--outline add-competitor-box"
                  onClick={() => add()}
                  disabled={fields.length >= MAX_COMPETITORS}
                >
                  <PlusOutlined /> {t("socialListingAgent.competitiveForm.addCompetitor")}
                </Button>

                <Button
                  type="primary"
                  htmlType="submit"
                  className="button button--primary"
                  disabled={!isComplete}
                >
                  {t("socialListingAgent.competitiveForm.startCompetitorAnalysis")}
                </Button>
              </div>
            </>
          )}
        </Form.List>
      </Form>
    </div>
  );
};

export default CompetitiveAnalysisForm;
